/**
 * NESTY BLOOM — Bloom Effect
 * Lluvia sutil de pétalos de sakura. Vanilla JS, sin librerías externas.
 *
 * Arquitectura:
 * - Pool fijo de elementos DOM creado una sola vez (nunca se crean/destruyen
 *   nodos después del arranque) — cada pétalo se "recicla" reasignándole
 *   nuevos valores aleatorios cuando sale de la pantalla por abajo.
 * - Un único bucle requestAnimationFrame anima TODOS los pétalos a la vez,
 *   escribiendo solo `transform` (traslación + rotación) vía JS directo —
 *   nunca se tocan propiedades que provoquen reflow (top/left/width/height
 *   se fijan una sola vez al crear/recicar el pétalo, no en cada frame).
 * - El número de pétalos activos se adapta: menos en móvil, menos si el
 *   hardware parece limitado, y se reduce en caliente si el FPS real cae
 *   de forma sostenida.
 *
 * Esquiva de contenido (genérica, no atada a ninguna sección):
 * - En vez de conocer secciones específicas del tema, el efecto consulta
 *   periódicamente el DOM por una lista de SELECTORES SEMÁNTICOS genéricos
 *   (títulos, párrafos, botones, campos de formulario) dentro del
 *   contenido principal (#MainContent), y guarda sus posiciones reales en
 *   pantalla como "zonas de exclusión". Cualquier sección nueva del tema
 *   queda cubierta automáticamente, sin agregar código por sección.
 * - Esa lista de zonas NO se recalcula en cada frame (sería costoso):
 *   se recalcula solo en scroll/resize (con throttle) y con una revisión
 *   periódica ligera, para detectar cambios de layout dinámicos (acordeones,
 *   menús, contenido que carga después).
 * - Cada pétalo, en su propio update() por frame, solo compara su posición
 *   contra esa lista ya calculada (una operación barata) y suaviza su
 *   desvío lateral con un "ease" en el tiempo, para que el rodeo se sienta
 *   como el viento curvándose alrededor del contenido, no como un salto.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var container = document.getElementById('nb-bloom-effect');
  if (!container || reduceMotion) return;

  // ---------------------------------------------------------------------
  // Configuración inicial: cantidad de pétalos según dispositivo/hardware
  // ---------------------------------------------------------------------
  var isMobile = window.matchMedia('(max-width: 749px)').matches;

  var lowEndHint =
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) ||
    (navigator.deviceMemory && navigator.deviceMemory <= 2);

  var MAX_COUNT = isMobile ? 11 : 22; // dentro de los rangos pedidos (8-12 / 15-25)
  if (lowEndHint) MAX_COUNT = Math.round(MAX_COUNT * 0.6);
  var MIN_COUNT = isMobile ? 6 : 10; // piso: nunca desaparece del todo

  var viewportW = window.innerWidth;
  var viewportH = window.innerHeight;

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  // ---------------------------------------------------------------------
  // Zonas de exclusión de contenido — genéricas, no atadas a ninguna sección
  // ---------------------------------------------------------------------
  // Selectores semánticos: cubren títulos, subtítulos, descripciones,
  // botones y formularios de CUALQUIER sección del tema automáticamente.
  var OBSTACLE_SELECTOR =
    '#MainContent h1, #MainContent h2, #MainContent h3, #MainContent h4, ' +
    '#MainContent h5, #MainContent h6, #MainContent p, #MainContent button, ' +
    '#MainContent [role="button"], #MainContent .nb-btn, #MainContent a.nb-btn, ' +
    '#MainContent input:not([type="hidden"]), #MainContent textarea, ' +
    '#MainContent select, #MainContent label, #MainContent form';

  var OBSTACLE_MARGIN = 20; // px de "colchón" alrededor de cada elemento
  var AVOID_MAX_OFFSET = 200; // tope de cuánto se puede desviar un pétalo
  var AVOID_EASE_RATE = 4.5; // qué tan rápido se suaviza el desvío (mayor = más ágil)

  var obstacles = []; // { left, top, right, bottom, cx } — coords de viewport

  function recomputeObstacles() {
    var nodes = document.querySelectorAll(OBSTACLE_SELECTOR);
    var next = [];
    for (var i = 0; i < nodes.length; i++) {
      var rect = nodes[i].getBoundingClientRect();
      // Solo interesan elementos con tamaño real y visibles dentro (o cerca)
      // del viewport actual — así la lista se mantiene chica sin importar
      // cuán larga sea la página.
      if (rect.width <= 0 || rect.height <= 0) continue;
      if (rect.bottom < -100 || rect.top > viewportH + 100) continue;

      next.push({
        left: rect.left - OBSTACLE_MARGIN,
        right: rect.right + OBSTACLE_MARGIN,
        top: rect.top - OBSTACLE_MARGIN,
        bottom: rect.bottom + OBSTACLE_MARGIN,
        cx: (rect.left + rect.right) / 2
      });
    }
    obstacles = next;
  }

  // ---------------------------------------------------------------------
  // Pétalo individual
  // ---------------------------------------------------------------------
  function Petal(el) {
    this.el = el;
    this.avoidOffset = 0; // desvío lateral suavizado por esquiva de contenido
    this.reset(true);
  }

  // `initial`: true solo en la primera creación, para repartir los pétalos
  // por toda la pantalla en vez de que todos empiecen arriba a la vez.
  Petal.prototype.reset = function (initial) {
    this.size = rand(7, 17); // px — la mayoría pequeños y discretos
    this.x = rand(-20, viewportW + 20);
    this.y = initial ? rand(-viewportH, viewportH) : rand(-90, -20);

    this.speed = rand(14, 34); // px/s de caída — lenta, no exagerada
    this.swayAmplitude = rand(8, 38); // cuánto lo mueve el "viento" a los lados
    this.swaySpeed = rand(0.25, 0.7); // qué tan rápido oscila ese vaivén
    this.swayPhase = rand(0, Math.PI * 2); // desfase para que no se muevan sincronizados

    this.rotation = rand(0, 360);
    this.rotationSpeed = rand(-38, 38); // deg/s — giro suave, en ambas direcciones

    this.opacity = rand(0.32, 0.82); // variación de opacidad entre pétalos
    // ~30% de los pétalos con un leve desenfoque, para dar sensación de profundidad
    this.blur = Math.random() < 0.3 ? rand(0.6, 1.6) : 0;

    this.avoidOffset = 0;

    var el = this.el;
    el.style.width = this.size + 'px';
    el.style.height = this.size + 'px';
    el.style.opacity = this.opacity;
    el.style.filter = this.blur ? 'blur(' + this.blur.toFixed(1) + 'px)' : '';
  };

  // Busca si la posición "natural" del pétalo cae dentro de alguna zona de
  // exclusión y, de ser así, devuelve el desvío deseado para rodearla por
  // el borde más cercano (izquierda o derecha, lo que quede más cerca).
  Petal.prototype.computeDesiredAvoidance = function (naturalX) {
    var halfSize = this.size / 2;
    for (var i = 0; i < obstacles.length; i++) {
      var o = obstacles[i];
      if (this.y + halfSize < o.top || this.y - halfSize > o.bottom) continue;
      if (naturalX + halfSize < o.left || naturalX - halfSize > o.right) continue;

      // Está dentro de la zona: rodear por el borde más cercano
      var toLeftEdge = naturalX - o.left; // distancia si sale por la izquierda
      var toRightEdge = o.right - naturalX; // distancia si sale por la derecha
      var target = toLeftEdge < toRightEdge ? o.left - halfSize : o.right + halfSize;
      return clamp(target - naturalX, -AVOID_MAX_OFFSET, AVOID_MAX_OFFSET);
    }
    return 0; // ninguna zona cerca: sin desvío
  };

  Petal.prototype.update = function (dt, elapsed) {
    this.y += this.speed * dt;
    this.rotation += this.rotationSpeed * dt;

    // Posición horizontal "natural" (sin esquivar nada todavía): base + viento
    var sway = Math.sin(elapsed * this.swaySpeed + this.swayPhase) * this.swayAmplitude;
    var naturalX = this.x + sway;

    // Esquiva de contenido: se suaviza en el tiempo (ease), para que el
    // rodeo se sienta orgánico — como el viento curvándose — en vez de un salto.
    var desired = this.computeDesiredAvoidance(naturalX);
    var ease = Math.min(1, dt * AVOID_EASE_RATE);
    this.avoidOffset += (desired - this.avoidOffset) * ease;

    var x = naturalX + this.avoidOffset;

    if (this.y > viewportH + 40) {
      this.reset(false);
      return;
    }

    this.el.style.transform =
      'translate3d(' + x.toFixed(1) + 'px,' + this.y.toFixed(1) + 'px,0) rotate(' + this.rotation.toFixed(1) + 'deg)';
  };

  // ---------------------------------------------------------------------
  // Crear el pool completo una sola vez (tamaño = MAX_COUNT)
  // ---------------------------------------------------------------------
  var petals = [];
  var fragment = document.createDocumentFragment();

  for (var i = 0; i < MAX_COUNT; i++) {
    var el = document.createElement('span');
    el.className = 'nb-bloom__petal';
    fragment.appendChild(el);
    petals.push(new Petal(el));
  }
  container.appendChild(fragment);

  // Cuántos del pool están activos ahora mismo (el resto queda oculto y
  // sin actualizar, listo para reactivarse si el rendimiento lo permite).
  var activeCount = MAX_COUNT;

  function setActiveCount(n) {
    activeCount = Math.max(MIN_COUNT, Math.min(MAX_COUNT, n));
    for (var i = 0; i < petals.length; i++) {
      petals[i].el.style.display = i < activeCount ? '' : 'none';
    }
  }
  setActiveCount(MAX_COUNT);

  // ---------------------------------------------------------------------
  // Bucle principal (un único requestAnimationFrame para todos los pétalos)
  // ---------------------------------------------------------------------
  var lastTime = null;
  var startTime = performance.now();

  // Monitor simple de rendimiento: si el tiempo promedio por frame indica
  // menos de ~30fps de forma sostenida, se reduce la cantidad de pétalos.
  var frameSamples = [];
  var lastSampleTime = null;

  function tick(now) {
    if (lastTime === null) lastTime = now;
    // Se limita el delta para evitar saltos grandes si la pestaña estuvo
    // en segundo plano (rAF se pausa solo, pero el reloj sigue corriendo).
    var dt = Math.min((now - lastTime) / 1000, 0.05);
    var elapsed = (now - startTime) / 1000;
    lastTime = now;

    for (var i = 0; i < activeCount; i++) {
      petals[i].update(dt, elapsed);
    }

    if (lastSampleTime !== null) {
      frameSamples.push(now - lastSampleTime);
      if (frameSamples.length >= 60) {
        var avg = frameSamples.reduce(function (a, b) { return a + b; }, 0) / frameSamples.length;
        frameSamples.length = 0;
        // ~33ms por frame equivale a ~30fps
        if (avg > 33 && activeCount > MIN_COUNT) {
          setActiveCount(activeCount - 4);
        }
      }
    }
    lastSampleTime = now;

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);

  // ---------------------------------------------------------------------
  // Recalcular zonas de exclusión: al cargar, en scroll/resize (throttled
  // con rAF) y con una revisión periódica ligera para layouts dinámicos
  // (acordeones, menús, contenido que carga después) sin acoplarse a
  // ningún otro componente del tema.
  // ---------------------------------------------------------------------
  var obstacleScanQueued = false;
  function queueObstacleScan() {
    if (obstacleScanQueued) return;
    obstacleScanQueued = true;
    requestAnimationFrame(function () {
      obstacleScanQueued = false;
      recomputeObstacles();
    });
  }

  recomputeObstacles();
  window.addEventListener('load', recomputeObstacles);
  window.addEventListener('scroll', queueObstacleScan, { passive: true });

  var resizeTimer;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        viewportW = window.innerWidth;
        viewportH = window.innerHeight;
        recomputeObstacles();
      }, 250);
    },
    { passive: true }
  );

  // Revisión periódica liviana: cubre cambios de layout que no disparan
  // scroll/resize (acordeones, contenido dinámico, imágenes que terminan
  // de cargar), sin que el efecto necesite conocer esos componentes.
  setInterval(recomputeObstacles, 1200);

  // Evita un salto brusco de posición cuando la pestaña vuelve a primer plano
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      lastTime = null;
      recomputeObstacles();
    }
  });
})();
